import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { Search, X } from 'lucide-react';
import { fetchChapters } from '../../services/quranAPI';
import { CrescentStarIcon } from '../common/IslamicIcons';
import { useQuranProgress } from '../../hooks/useQuranProgress';

const REVELATION_PLACE = {
  makkah:  { en: 'Meccan',  ar: 'مكية', color: 'text-amber-500',  bg: 'bg-amber-500/15 border-amber-500/25' },
  madinah: { en: 'Medinan', ar: 'مدنية', color: 'text-sky-500',    bg: 'bg-sky-500/15 border-sky-500/25' },
};

const FullQuranBrowser = () => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL, language } = useTheme();
  const navigate = useNavigate();

  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chapterError, setChapterError] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // 'all'|'makkah'|'madinah'

  useEffect(() => {
    fetchChapters()
      .then(setChapters)
      .catch(() => setChapterError(true))
      .finally(() => setLoading(false));
  }, []);

  const filtered = chapters.filter(c => {
    const matchFilter = filter === 'all' || c.revelation_place === filter;
    const q = search.toLowerCase();
    const matchSearch = !search ||
      c.name_simple.toLowerCase().includes(q) ||
      c.name_arabic.includes(search) ||
      c.translated_name?.name.toLowerCase().includes(q) ||
      String(c.id).includes(q);
    return matchFilter && matchSearch;
  });

  const { getLastRead } = useQuranProgress();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-[var(--p-hex)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (chapterError) {
    return (
      <div className={`rounded-2xl p-8 text-center ${isDarkMode ? 'glass-card' : 'bg-white shadow-sm border border-gray-100'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Failed to load chapters. Check your connection.</p>
        <button onClick={() => { setChapterError(false); setLoading(true); fetchChapters().then(setChapters).catch(() => setChapterError(true)).finally(() => setLoading(false)); }}
          className="mt-3 px-4 py-2 rounded-xl bg-[var(--p-hex)] text-white text-sm">Retry</button>
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="space-y-3">
      {/* Search + filter */}
      <div className="space-y-2">
        <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl ${
          isDarkMode ? 'bg-white/8 border border-white/10' : 'bg-white border border-gray-200 shadow-sm'
        }`}>
          <Search className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={language === 'ar' ? 'ابحث عن سورة...' : 'Search surah...'}
            className={`flex-1 bg-transparent text-sm outline-none ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-800 placeholder-gray-400'}`}
          />
          {search && <button onClick={() => setSearch('')}><X className="w-3.5 h-3.5 text-gray-400" /></button>}
        </div>
        <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {['all','makkah','madinah'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`flex-1 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                filter === f
                  ? 'bg-[var(--p-hex)] text-white'
                  : isDarkMode ? 'bg-white/8 text-gray-400 hover:bg-white/15' : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-100'
              }`}>
              {f === 'all' ? (language === 'ar' ? 'الكل' : 'All') : REVELATION_PLACE[f]?.[language === 'ar' ? 'ar' : 'en'] || f}
            </button>
          ))}
        </div>
        <p className={`text-[10px] px-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
          {filtered.length} of 114 surahs
        </p>
      </div>

      {/* Surah list */}
      <div className="space-y-2 stagger card-grid">
        {filtered.map(c => {
        const cardClass = isDarkMode
          ? 'glass-card hover:bg-white/8 cursor-pointer'
          : 'bg-white shadow-sm border border-gray-100 hover:shadow-md cursor-pointer';

        const lastRead = getLastRead(c.id);
        const rev = REVELATION_PLACE[c.revelation_place];
        return (
            <button
              key={c.id}
              data-testid={`surah-${c.id}`}
              onClick={() => navigate(`/quran/surah/${c.id}`)}
              className={`w-full flex items-center gap-3 p-3 rounded-2xl transition-all hover-lift ${cardClass} ${isRTL ? 'flex-row-reverse text-right' : ''}`}
            >
              {/* Number badge */}
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm tabular-nums"
                style={{ background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'var(--bg-light-from)', color: isDarkMode ? '#fbbf24' : 'var(--p-hex)' }}>
                {c.id}
              </div>
              {/* Names */}
              <div className="flex-1 min-w-0 text-left">
                <p className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {c.name_simple}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {c.translated_name?.name} · {c.verses_count} verses
                  {lastRead && (
                    <span className={`ml-1.5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                      · {lastRead.verse_key}
                    </span>
                  )}
                </p>
              </div>
              {/* Arabic name */}
              <div className={`flex flex-col items-end gap-1 flex-shrink-0 ${isRTL ? 'items-start' : ''}`}>
                <p className="font-semibold text-base"
                  style={{ fontFamily: "'Amiri', 'Noto Naskh Arabic', serif", color: isDarkMode ? '#fbbf24' : 'var(--p-hex)' }}>
                  {c.name_arabic}
                </p>
                {rev && (
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${rev.bg} ${rev.color}`}>
                    {rev[language === 'ar' ? 'ar' : 'en']}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FullQuranBrowser;
